import { listSubscriptions, listOnetimes, listCharges, getCharge, listAddresses, listPaymentMethods, createAddress as createAddressSDK, activateSubscription, updateSubscriptionChargeDate, getCreditSummary, listCreditAccounts, removeDiscountsFromAddress, applyDiscountToAddress, updateSubscription as updateSubscriptionSDK, setApplyCreditsToNextCharge, listBundleSelections, getBundleSelection as getBundleSelectionSDK, updateBundleSelection, createOnetime, deleteOnetime as deleteOnetimeSDK, updateOnetime as updateOnetimeSDK, getSubscription, updateBundle as updateBundleSDK, processCharge as processChargeSDK, createSubscription as createSubscriptionSDK, getStoreSettings as getStoreSettingsSDK, cancelSubscription as cancelSubscriptionSDK, updateAddress as updateAddressSDK, updateSubscriptionAddress as updateSubscriptionAddressSDK, getCustomer as getCustomerSDK, getGiftPurchase, listGiftPurchases, deleteBundleSelection as deleteBundleSelectionSDK } from '@rechargeapps/storefront-client';
import { loginWithShopifyStorefront as loginWithShopifyStorefrontSDK, initFrictionlessPaymentV1, updateCustomer as updateCustomerSDK } from '@rechargeapps/storefront-client';
import { trackRescheduledEvent, retryErrorCharge, recordUserEvent, logPortalError, BASE_API_URL, fetchWildgrainApi } from './api';
import { trackPush } from '../utils/reschedulePushTracker';
import { isProxyMode, isNetworkLevelError, withProxyFallback } from '../utils/rechargeProxyMode';
// Add-on add/remove/quantity analytics live on the onetime mutations below —
// the one path every surface (builder, overview picker, product modal, deals
// modal, curated bar) funnels through. Reads feed its context/registry.
import {
  rememberCustomer,
  rememberSubscriptions,
  rememberOnetimes,
  trackAddonAdded,
  trackAddonRemoved,
  trackAddonQuantityChanged,
} from './boxChangeEvents';
// When authentication session is missing we want to immediately log the user out
// and redirect them to the login screen so they can re-authenticate.
// We'll clear local data here and notify the app to show the session-expired modal
// instead of immediately redirecting, letting the user finish interactions smoothly.
import { logout } from '../utils/auth';

// ---------------------------------------------------------------------------
// Shared cache (see sharedCache.js)
// ---------------------------------------------------------------------------
// Reads route through the app-wide query cache: in-flight dedup, 30s TTL by
// default, and mutations invalidate by entity so the next read always fetches
// fresh data (see invalidateCache).
import { cachedFetch, invalidateSharedCache } from './sharedCache';

// Reads that every page depends on get the same transport fallback the login
// already has: when the direct Recharge connection dies mid-fetch (WebKit
// "Load failed"), flag this device onto the first-party proxy, re-init the SDK
// and retry once. Clarity showed members logging in fine and then stuck on
// "Couldn't load this page" / "Unable to load account details" because only
// the login call knew how to fall back. Writes are left direct: a retry after
// a connection that died mid-request could apply a change twice.
const readViaProxyFallback = (call) => withProxyFallback(call);

const CUSTOMER_TTL_MS = 60_000;

/**
 * Clear the entire shared cache, or only entries for one entity
 * ('subscriptions', 'charges', …). Called automatically by mutation helpers
 * so pages that re-fetch after an action always receive fresh data.
 */
export const invalidateCache = (entity) => invalidateSharedCache(entity);

// Box previews (api.js getBoxContent / getSubscriptionBundleContent) are
// derived from subscription state. Their cache keys cover every input the
// portal sends, but drop them on any box-affecting mutation as well so a
// key-identical refetch right after an edit can never serve a stale preview.
const invalidateBoxPreviews = () => {
  invalidateSharedCache('boxContent');
  invalidateSharedCache('bundleContent');
};

const notifySessionExpired = () => {
  try {
    // Clear just the Recharge session key; full logout (redirect) happens when
    // the user acknowledges the modal.
    localStorage.removeItem('rechargeSession');
  } catch {}
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('session-expired'));
  }
};

const getRechargeSession = () => {
  const rechargeSessionStr = localStorage.getItem('rechargeSession');

  if (!rechargeSessionStr) {
    // Proactively log the user out so the UI can display the login screen /
    // session-expired modal as defined in the App component logic.
    notifySessionExpired();
    throw new Error('No authentication session found');
  }

  const rechargeSession = JSON.parse(rechargeSessionStr);

  // A session with a token but no customer (Recharge issues these when the
  // Shopify customer token it was given is no longer valid) can never load
  // member data: the SDK throws "Not logged in." on customer-scoped reads
  // before making a request. Clear it and send the member back to login.
  if (!rechargeSession || !rechargeSession.apiToken || !rechargeSession.customerId) {
    notifySessionExpired();
    throw new Error('Invalid session format');
  }

  return rechargeSession;
};

// WGS_ seasonal bundles are add-on subscriptions that ride along with the box —
// they must never render as a membership/box card anywhere in the portal.
export const isSeasonalSubscription = (subscription) =>
  (subscription?.sku || '').toUpperCase().startsWith('WGS_');

const fetchAllSubscriptions = async (options = {}) => {
  const subscriptions = await cachedFetch(['subscriptions', options], async () => {
    const session = getRechargeSession();
    const defaultOptions = {
      limit: 25,
      sort_by: 'id-asc',
      include: ['bundle_selections', 'address', 'customer'],
    };

    const response = await readViaProxyFallback(() => listSubscriptions(session, { ...defaultOptions, ...options }));

    // Filter out expired subscriptions
    const activeSubscriptions = response.subscriptions.filter(subscription => subscription.status !== 'expired');
    return activeSubscriptions;
  });
  rememberSubscriptions(subscriptions);
  return subscriptions;
};

export const fetchSubscriptions = async (options = {}) => {
  const subscriptions = await fetchAllSubscriptions(options);
  return subscriptions.filter((subscription) => !isSeasonalSubscription(subscription));
};

// The seasonal bundle add-on subscription(s) hidden from fetchSubscriptions.
export const fetchSeasonalSubscriptions = async (options = {}) => {
  const subscriptions = await fetchAllSubscriptions(options);
  return subscriptions.filter(isSeasonalSubscription);
};

export const fetchSubscription = async (subscriptionId) => {
  const session = getRechargeSession();
  const subscription = await readViaProxyFallback(() => getSubscription(session, subscriptionId, { include: ['bundle_selections', 'address', 'customer'] }));
  rememberSubscriptions(subscription);
  return subscription;
};

export const fetchOnetimes = async (options = {}) => {
  const onetimes = await cachedFetch(['onetimes', options], async () => {
    const session = getRechargeSession();
    const defaultOptions = {
      limit: 25,
      sort_by: 'id-asc',
    };

    const response = await readViaProxyFallback(() => listOnetimes(session, { ...defaultOptions, ...options }));
    return response?.onetimes || [];
  });
  rememberOnetimes(onetimes);
  return onetimes;
};

export const fetchCharges = async (options = {}) => {
  return cachedFetch(['charges', options], async () => {
    const session = getRechargeSession();
    const defaultOptions = {
      // Recharge's max. A failed charge blocks the address for as long as it
      // exists, however old it is — with only the 25 newest charges a tenured
      // member's dead failure never even loaded.
      limit: 250,
      sort_by: 'scheduled_at-desc',
    };

    const response = await readViaProxyFallback(() => listCharges(session, { ...defaultOptions, ...options }));
    
    // Filter out refunded and cancelled charges
    // A 204/empty body from Recharge leaves `response` undefined; treat it as no charges.
    const filteredCharges = (response?.charges || []).filter(charge => {
      const status = (charge.status || '').toLowerCase();
      return status !== 'refunded' && status !== 'cancelled';
    });
    
    return filteredCharges;
  });
};

// "Order Today" (services/api processCharge) only ENQUEUES the charge on the
// backend and returns 200 straight away — Recharge processes it a few seconds
// later. A refetch fired as soon as that request resolves therefore still
// sees the queued charge and the old next charge date, and the page re-renders
// the pre-order state. Poll (uncached) until the charge has left `queued` and
// Recharge has moved the subscription's next_charge_scheduled_at, then drop
// the affected caches so the caller's refetch is guaranteed fresh. Bounded:
// gives up after attempts × intervalMs (~20s) and lets the caller refetch
// anyway. Resolves true when the new state was observed, false on timeout.
// Never throws: by the time this runs the order IS placed, so a read failure
// here must not surface as an order error.
export const waitForOrderProcessed = async (
  { chargeId, subscriptionId, previousChargeDate },
  { attempts = 10, intervalMs = 2000 } = {}
) => {
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  let session = null;
  try {
    session = getRechargeSession();
  } catch {
    // No session to poll with — the caller's refetch surfaces the login flow.
  }
  let chargeDone = false;
  let settled = false;

  for (let attempt = 0; session && attempt < attempts && !settled; attempt++) {
    await sleep(intervalMs);
    try {
      if (!chargeDone) {
        const chargeRes = await getCharge(session, chargeId);
        const charge = chargeRes?.charge || chargeRes;
        const status = (charge?.status || '').toLowerCase();
        chargeDone = !!status && status !== 'queued';
        // A failed payment never moves the date — nothing more to wait for.
        if (status === 'error') settled = true;
      }
      if (chargeDone && !settled) {
        if (!subscriptionId) {
          settled = true;
        } else {
          const subRes = await getSubscription(session, subscriptionId);
          const sub = subRes?.subscription || subRes;
          settled = !!sub && sub.next_charge_scheduled_at !== previousChargeDate;
        }
      }
    } catch (err) {
      // Transient read failure — keep polling until the deadline.
      console.warn('waitForOrderProcessed: retrying after read error', err);
    }
  }

  invalidateCache('charges');
  invalidateCache('subscriptions');
  invalidateCache('onetimes');
  // The Shopify order behind the "Last order" card (see shopify.js).
  invalidateCache('customerOrders');
  return settled;
};

export const fetchAddresses = async (options = {}) => {
  return cachedFetch(['addresses', options], async () => {
    const session = getRechargeSession();
    const defaultOptions = {
      limit: 25,
      sort_by: 'id-asc',
      // include: ['discount'],
    };

    const response = await readViaProxyFallback(() => listAddresses(session, { ...defaultOptions, ...options }));
    return response?.addresses || [];
  });
};

export const reactivateSubscription = async (subscriptionId) => {
  const session = getRechargeSession();
  const result = await activateSubscription(session, subscriptionId);
  invalidateCache('subscriptions');
  invalidateCache('charges');
  return result;
};

// Set a subscription's charge date to an exact store-local calendar day
// (YYYY-MM-DD). Unlike rescheduleSubscription this does not shift the date by
// a day and does not move add-ons: campaign activations call it right after a
// reactivation, before any add-on rides on the box.
export const setSubscriptionChargeDate = async (subscriptionId, date) => {
  const session = getRechargeSession();
  const result = await updateSubscriptionChargeDate(session, subscriptionId, date);
  invalidateCache('subscriptions');
  invalidateCache('charges');
  invalidateBoxPreviews();
  return result;
};

export const getUniqueAddresses = (addresses) => {
  const uniqueAddresses = [];
  for (const address of addresses) {
    if (!uniqueAddresses.some((a) => a.address1 === address.address1 && a.zip === address.zip)) {
      uniqueAddresses.push(address);
    }
  }
  return uniqueAddresses;
};

// True when two Recharge date strings fall on the same calendar day.
const sameChargeDay = (a, b) => !!a && !!b && String(a).slice(0, 10) === String(b).slice(0, 10);

// Add-on onetimes carry their OWN `next_charge_scheduled_at` in Recharge —
// moving only the subscription leaves them behind on the old date, where
// Recharge keeps a separate charge. The member then still sees (and gets
// billed for) a shipment on the date they just moved away from: the
// "I changed my date but it switched back" complaint. Best-effort by design:
// by the time this runs the subscription date IS moved, so failures are
// logged to the API instead of surfacing as a reschedule error.
const moveAddonsWithReschedule = async (session, { subscriptionId, addressId, oldChargeDate, newDate }) => {
  if (!addressId || !oldChargeDate || sameChargeDay(oldChargeDate, newDate)) return;

  let rows = [];
  try {
    const response = await listOnetimes(session, { limit: 50, sort_by: 'id-asc' });
    rows = (response?.onetimes || []).filter((ot) =>
      String(ot.address_id) === String(addressId) &&
      sameChargeDay(ot.next_charge_scheduled_at, oldChargeDate)
    );
  } catch (error) {
    console.error('Reschedule: failed to list add-ons to move with the box:', error);
    logPortalError('Reschedule - listing add-ons to move failed', error, {
      subscriptionId, addressId, oldChargeDate, newDate,
    }).catch(() => {});
    return;
  }

  for (const row of rows) {
    try {
      await updateOnetimeSDK(session, row.id, { next_charge_scheduled_at: newDate });
    } catch (error) {
      console.error(`Reschedule: failed to move add-on ${row.id} to ${newDate}:`, error);
      logPortalError('Reschedule - moving add-on failed', error, {
        subscriptionId,
        onetimeId: row.id,
        productTitle: row.product_title || null,
        oldChargeDate,
        newDate,
      }).catch(() => {});
    }
  }

  if (rows.length) {
    invalidateCache('onetimes');
  }
};

// `previousChargeDate` (the charge date before this reschedule) powers the
// consecutive-push tracking behind the reschedule-loop experiment: pushing
// LATER increments the per-subscription streak, moving EARLIER resets it.
// Callers that can't supply it just skip the streak update.
export const rescheduleSubscription = async (subscriptionId, newDate, previousChargeDate = null) => {
  const newDateWith1Day = new Date(newDate);
  newDateWith1Day.setDate(newDateWith1Day.getDate() + 1);
  newDate = newDateWith1Day.toISOString().split('T')[0];

  const session = getRechargeSession();

  // Pre-move state feeds the add-on move below: the rows riding on this
  // box's charge are identified by (address, old charge date).
  let addressId = null;
  let oldChargeDate = previousChargeDate;
  try {
    const subResponse = await getSubscription(session, subscriptionId);
    const sub = subResponse?.subscription || subResponse;
    addressId = sub?.address_id ?? null;
    oldChargeDate = sub?.next_charge_scheduled_at || oldChargeDate;
  } catch (error) {
    console.warn('Reschedule: could not read subscription before moving date:', error);
  }

  const result = await updateSubscriptionChargeDate(session, subscriptionId, newDate);
  await moveAddonsWithReschedule(session, { subscriptionId, addressId, oldChargeDate, newDate });
  invalidateCache('subscriptions');
  invalidateCache('charges');
  invalidateBoxPreviews();

  // Consecutive-push streak (localStorage; see utils/reschedulePushTracker).
  let movedLater = null;
  let pushCount = null;
  if (previousChargeDate) {
    const prev = String(previousChargeDate).slice(0, 10);
    movedLater = newDate > prev;
    pushCount = trackPush(subscriptionId, movedLater);
  }

  // Fire analytics (non-blocking). Both events carry the redesign variant so
  // reschedule rates and push depth are comparable old vs new portal.
  (async () => {
    try {
      const subscription = await getSubscription(session, subscriptionId, { include: ['customer'] });
      const customerEmail = subscription?.include?.customer?.email;
      const currentMonth = new Date().toISOString().slice(0, 7);
      const newDateMonth = newDate.slice(0, 7);
      const rescheduledOutOfCurrentMonth = newDateMonth > currentMonth;
      recordUserEvent({
        email: customerEmail || null,
        subscriptionId,
        eventName: 'reschedule',
        step: 'charge_date_updated',
        action: movedLater === null ? null : movedLater ? 'moved_later' : 'moved_earlier',
        properties: {
          new_date: newDate,
          rescheduled_out_of_current_month: rescheduledOutOfCurrentMonth,
          ...(pushCount === null ? {} : { push_count: pushCount }),
        },
      });
      if (customerEmail) {
        await trackRescheduledEvent(customerEmail, subscriptionId, newDate, rescheduledOutOfCurrentMonth, {
          ...(pushCount === null ? {} : { push_count: pushCount }),
          ...(movedLater === null ? {} : { moved_later: movedLater }),
        });
      }
    } catch (err) {
      console.error('Failed to send reschedule analytics:', err);
    }
  })();

  return result;
};

// The credit summary is one request that already carries every credit
// account (`include.credit_details`, each with its own `expires_at`), so the
// total and the per-account breakdown share a single cached fetch. Keep
// 'creditBalance' as the entity name: mutations invalidate by that key.
export const fetchCreditSummary = async (options = {}) => {
  return cachedFetch(['creditBalance', options], async () => {
    const session = getRechargeSession();
    const defaultOptions = {
      include: ['credit_details'],
    };

    return readViaProxyFallback(() => getCreditSummary(session, { ...defaultOptions, ...options }));
  });
};

export const fetchCreditBalance = async (options = {}) => {
  const summary = await fetchCreditSummary(options);
  return summary?.total_available_balance;
};

export const fetchCreditAccounts = async (options = {}) => {
  return cachedFetch(['creditAccounts', options], async () => {
    const session = getRechargeSession();
    const defaultOptions = {
      limit: 25,
      sort_by: 'id-asc',
    };

    const response = await readViaProxyFallback(() => listCreditAccounts(session, { ...defaultOptions, ...options }));
    return response.credit_accounts;
  });
};

export const removeDiscount = async (addressId) => {
  const session = getRechargeSession();
  const result = await removeDiscountsFromAddress(session, addressId);
  invalidateCache('addresses');
  // The displayed discount is derived from the queued charge's `discounts`,
  // so charges must be re-fetched fresh or the removed discount reappears.
  invalidateCache('charges');
  return result;
};

export const applyDiscount = async (addressId, discountCode) => {
  const session = getRechargeSession();
  const result = await applyDiscountToAddress(session, addressId, discountCode);
  invalidateCache('addresses');
  invalidateCache('charges');
  return result;
};

export const updateSubscription = async (subscriptionId, updateData) => {
  const session = getRechargeSession();
  const result = await updateSubscriptionSDK(session, subscriptionId, updateData);
  invalidateCache('subscriptions');
  invalidateBoxPreviews();
  return result;
};

export const applyCreditsToNextCharge = async (recurring = true) => {
  const session = getRechargeSession();
  const result = await setApplyCreditsToNextCharge(session, { recurring });
  invalidateCache('creditBalance');
  return result;
};

export const getAppliedCredits = (subscription, charges, credits) => {
  if (!subscription || !charges || charges.length === 0) {
    return 0;
  }

  const charge = charges.find((c) => c.address_id === subscription.include.address.id);
  if (!charge) return 0;

  // According to Recharge, `total_price` already reflects discounts but not
  // credits. Therefore we can simply cap credits to this value without
  // manually subtracting the discount again (avoids double-counting).
  const totalAfterDiscount = parseFloat(charge.total_price || 0);

  const creditsAvailable = parseFloat(credits || 0);
  return Math.min(creditsAvailable, totalAfterDiscount);
};

export const getBundleSelection = async (bundleSelectionId) => {
  const session = getRechargeSession();
  return await getBundleSelectionSDK(session, bundleSelectionId);
};

export const fetchBundleSelections = async (options = {}) => {
  return cachedFetch(['bundleSelections', options], async () => {
    const session = getRechargeSession();
    const defaultOptions = {
      limit: 25,
      sort_by: 'id-asc',
    };

    const response = await readViaProxyFallback(() => listBundleSelections(session, { ...defaultOptions, ...options }));
    return response.bundle_selections;
  });
};

export const updateBundleSelectionItems = async (bundleSelectionId, items) => {
  const session = getRechargeSession();
  const result = await updateBundleSelection(session, bundleSelectionId, {
    items
  });
  invalidateCache('bundleSelections');
  invalidateCache('subscriptions');
  invalidateBoxPreviews();
  return result;
};

// Recharge requires external ids as STRINGS ({"ecommerce": "Not a valid
// string."} otherwise). Product objects reach the onetime path from two
// catalogs: the Storefront API (ids cleaned to strings) and the wildgrain-api
// bundle config that feeds the plan grids (numeric ids). A plan-grid row that
// gets converted into an add-on — the box-full conversion, the plan-slot cap,
// the "help me choose" picks — carried its numeric variant id through here.
const firstVariantIdString = (productData) => {
  const raw = productData?.variants?.[0]?.id;
  if (raw === null || raw === undefined || raw === '') return null;
  return String(raw);
};

export const createOnetimeItem = async (addressId, productData, nextChargeDate = null, properties = [], priceOverride = null) => {
  const desiredPriceStr = priceOverride !== null && priceOverride !== undefined
    ? (typeof priceOverride === 'number' ? priceOverride.toFixed(2) : String(priceOverride))
    : null;
  // Threshold-tier bonuses (and any future free-onetime use case) need
  // `price = 0` in Recharge, but the customer-portal storefront token
  // CANNOT override price on POST /onetimes or PUT /onetimes/{id} — Recharge
  // silently stores the variant price (verified empirically with both string
  // and numeric formats on both endpoints). When a caller asks for a price
  // override, we route through wildgrain-api which uses the admin Recharge
  // token server-side. The endpoint hardcodes `price: "0.00"` and only
  // accepts requests carrying a `_tier_bonus` property, so this isn't a
  // generic price-override gadget.
  if (desiredPriceStr !== null && parseFloat(desiredPriceStr) === 0) {
    return await createZeroPricedOnetimeViaProxy(addressId, productData, nextChargeDate, properties);
  }

  const session = getRechargeSession();

  const data = {
    address_id: addressId,
    quantity: productData.quantity,
    external_product_id: {
      ecommerce: String(productData.id)
    },
    product_title: productData.title
  };

  if (properties && properties.length) {
    data.properties = properties;
  }

  if (nextChargeDate) {
    data.next_charge_scheduled_at = nextChargeDate;
  } else {
    data.add_to_next_charge = true;
  }

  const variantId = firstVariantIdString(productData);
  if (variantId) {
    data.external_variant_id = {
      ecommerce: variantId
    };
  }

  const result = await createOnetime(session, data);
  invalidateCache('onetimes');
  invalidateCache('charges');
  trackAddonAdded(result, productData);
  return result;
};

// Internal helper: proxy a $0 onetime creation through wildgrain-api.
// The backend ignores any client-supplied price and always sets it to
// "0.00", and rejects requests missing a `_tier_bonus` property, so this
// path is intentionally narrow.
const createZeroPricedOnetimeViaProxy = async (addressId, productData, nextChargeDate, properties) => {
  const variantId = firstVariantIdString(productData);
  if (!variantId) {
    throw new Error('createOnetimeItem: cannot proxy zero-priced onetime — variant id missing');
  }
  const body = {
    address_id: addressId,
    quantity: productData.quantity,
    external_product_id: { ecommerce: String(productData.id) },
    external_variant_id: { ecommerce: variantId },
    product_title: productData.title,
    properties: properties || [],
    next_charge_scheduled_at: nextChargeDate || undefined,
  };

  // eslint-disable-next-line no-console
  console.log('[recharge.createOnetimeItem] proxy → POST /api/recharge/onetime', body);
  const response = await fetchWildgrainApi(`${BASE_API_URL}/api/recharge/onetime`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch (parseErr) {
    payload = null;
  }
  if (!response.ok) {
    const detail = payload && (payload.error || payload.details);
    // eslint-disable-next-line no-console
    console.error('[recharge.createOnetimeItem] proxy ←', response.status, payload);
    throw new Error(`createOnetimeItem proxy failed (${response.status}): ${detail || 'unknown error'}`);
  }
  const onetime = payload?.onetime || null;
  // eslint-disable-next-line no-console
  console.log('[recharge.createOnetimeItem] proxy ←', {
    id: onetime?.id,
    price: onetime?.price,
    product_title: onetime?.product_title,
    properties: onetime?.properties,
  });
  invalidateCache('onetimes');
  invalidateCache('charges');
  trackAddonAdded(onetime, productData);
  return onetime;
};

export const deleteOnetime = async (onetimeId) => {
  const session = getRechargeSession();
  const result = await deleteOnetimeSDK(session, onetimeId);
  invalidateCache('onetimes');
  invalidateCache('charges');
  trackAddonRemoved(onetimeId);
  return result;
};

export const updateOnetime = async (onetimeId, updateData) => {
  const session = getRechargeSession();
  const result = await updateOnetimeSDK(session, onetimeId, updateData);
  invalidateCache('onetimes');
  invalidateCache('charges');
  trackAddonQuantityChanged(onetimeId, updateData, result);
  return result;
};

export const updateBundle = async (bundleSelectionId, updateData) => {
  const session = getRechargeSession();

  // Ensure we are including collection_id for each item if available
  const preparedData = { ...updateData };

  try {
    const result = await updateBundleSDK(session, bundleSelectionId, preparedData, { commit: true });
    invalidateCache('subscriptions');
    invalidateCache('bundleSelections');
    invalidateBoxPreviews();
    return result;
  } catch (error) {
    console.error('Error updating bundle:', error);
    throw error;
  }
};

export const createNewAddress = async (addressData) => {
  const session = getRechargeSession();
  const result = await createAddressSDK(session, addressData);
  invalidateCache('addresses');
  return result;
};

export const processGiftCard = async ({ addressId: explicitAddressId, variantId, recipientFirstName = '', recipientLastName = '', recipientEmail = '', recipientNote = '', notificationDate = '' }) => {
  const session = getRechargeSession();

  // Determine which address to use for billing/processing.
  let addressId = explicitAddressId;

  if (!addressId) {
    try {
      // 1) Try first subscription's address
      const subsRes = await listSubscriptions(session, { limit: 50, sort_by: 'id-asc', include: ['address'] });
      const subs = subsRes?.subscriptions || subsRes || [];
      const subWithAddress = subs.find((s) => s.address_id);
      if (subWithAddress) {
        addressId = subWithAddress.address_id;
      }
    } catch (err) {
      console.warn('Unable to retrieve subscriptions while determining gift card address', err);
    }
  }

  if (!addressId) {
    try {
      const addrRes = await listAddresses(session, { limit: 50, sort_by: 'id-asc' });
      const addrs = addrRes?.addresses || addrRes || [];
      const addrWithPayment = addrs.find((a) => a.payment_method_id);
      if (addrWithPayment) {
        addressId = addrWithPayment.id;
      } else if (addrs.length) {
        addressId = addrs[0].id; // fallback to first
      }
    } catch (err) {
      console.warn('Unable to retrieve addresses while determining gift card address', err);
    }
  }

  if (!addressId) {
    throw new Error('Unable to determine a valid address to process the gift card');
  }

  // Build properties array expected by legacy logic / flows
  const properties = [
    { name: '__rc_gift_recipient_first_name', value: recipientFirstName || 'First Name' },
    { name: '__rc_gift_recipient_last_name', value: recipientLastName || 'Last Name' },
    { name: '__rc_gift_recipient_email', value: recipientEmail || '' },
    { name: '__rc_gift_recipient_note', value: recipientNote || '' },
    { name: '__rc_gift_notification_scheduled_at', value: notificationDate || '' },
  ];

  // 1. Create a onetime line-item representing the gift card
  const today = new Date();
  await createOnetime(session, {
    address_id: addressId,
    next_charge_scheduled_at: today.toISOString(),
    quantity: 1,
    properties,
    external_variant_id: {
      ecommerce: variantId,
    },
  });

  // 2. Retrieve latest charges to locate queued, onetime-only charge
  const chargesResponse = await listCharges(session, {});
  const charges = chargesResponse?.charges || chargesResponse || [];

  // Filter out refunded and cancelled charges
  const filteredCharges = charges.filter(charge => {
    const status = (charge.status || '').toLowerCase();
    return status !== 'refunded' && status !== 'cancelled';
  });

  const queuedOnetimeCharges = filteredCharges
    .filter((c) => (c.status || '').toUpperCase() === 'QUEUED')
    .filter((c) => Array.isArray(c.line_items) && c.line_items.every((li) => li.purchase_item_type === 'onetime'));

  if (!queuedOnetimeCharges.length) return;

  // Process the earliest such charge (closest scheduled_at date)
  const earliestCharge = queuedOnetimeCharges.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
  if (typeof processChargeSDK === 'function') {
    await processChargeSDK(session, earliestCharge.id);
  } else {
    console.warn('processCharge function not available in SDK. The gift card charge may remain queued.');
  }
  invalidateCache('charges');
  invalidateCache('onetimes');
};

export const loginWithShopifyStorefront = async (shopifyStorefrontAccessToken, shopifyCustomerAccessToken) => {
  try {
    const session = await loginWithShopifyStorefrontSDK(shopifyStorefrontAccessToken, shopifyCustomerAccessToken);
    // A new session may belong to a different customer (login, impersonation),
    // so nothing cached under the old session is safe to reuse.
    invalidateCache();
    return session;
  } catch (error) {
    console.error('Error logging into Recharge with Shopify storefront:', error);
    throw error;
  }
};

/**
 * Collapse duplicate payment method records that point at the same physical
 * card. On a Shopify Payments store, Recharge mints a NEW payment method
 * record whenever a card is re-entered (frictionless edit/make-primary, or
 * repeat checkouts), while Shopify merges them into one vault entry — so the
 * duplicates are cosmetic. We group by card identity (brand + last4,
 * deliberately ignoring expiry so a renewed card still merges with its old
 * record) and keep the most relevant record: the default one, else newest.
 */
const dedupePaymentMethods = (methods) => {
  const groups = new Map();
  for (const pm of methods) {
    const details = pm.payment_details || {};
    // Normalize aggressively: different record sources store the brand
    // differently (e.g. "american_express" vs "American Express"), and last4
    // can be a number or string.
    const brandKey = String(details.brand || '').toLowerCase().replace(/[^a-z]/g, '');
    const last4Key = String(details.last4 || '').trim();
    const key = brandKey && last4Key
      ? `${brandKey}|${last4Key}`
      : `id:${pm.id}`; // no card details — don't risk merging
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, pm);
      continue;
    }
    let winner;
    if (Boolean(pm.default) !== Boolean(existing.default)) {
      winner = pm.default ? pm : existing;
    } else {
      winner = (pm.id || 0) > (existing.id || 0) ? pm : existing;
    }
    groups.set(key, winner);
  }
  return Array.from(groups.values());
};

// Fetch payment methods for the authenticated customer using the Recharge SDK
export const fetchPaymentMethods = async (options = {}) => {
  return cachedFetch(['paymentMethods', options], async () => {
    const session = getRechargeSession();
    const defaultOptions = {
      limit: 25,
      sort_by: 'id-asc',
    };

    const response = await readViaProxyFallback(() => listPaymentMethods(session, { ...defaultOptions, ...options }));

    // The SDK may return the array under either `payment_methods` or `paymentMethods`
    const methods = response?.payment_methods || response?.paymentMethods || [];
    // eslint-disable-next-line no-console
    console.debug('[recharge] payment methods (pre-dedup):', methods.map((pm) => ({
      id: pm.id,
      brand: pm.payment_details?.brand,
      last4: pm.payment_details?.last4,
      default: pm.default,
      processor: pm.processor_name,
    })));
    return dedupePaymentMethods(methods);
  });
};

export const createSubscription = async (addressId, options = {}) => {
  const session = getRechargeSession();

  const fiveDaysFromNow = new Date();
  fiveDaysFromNow.setDate(fiveDaysFromNow.getDate() + 5);

  const data = {
    address_id: addressId,
    next_charge_scheduled_at: fiveDaysFromNow.toISOString(),
    plan_id:15892016,
    quantity: 1,
    external_variant_id: {
      ecommerce: '41420078612547',
    },
    external_product_id: {
      ecommerce: '7161005736003',
    },
    ...options, // allow overriding default fields if needed
  };

  try {
    const result = await createSubscriptionSDK(session, data);
    invalidateCache('subscriptions');
    return result;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

// Creates the WGS_ seasonal bundle add-on subscription on the member's own
// session (so it can only ever touch their own account). It is tied to the
// active box subscription at the address: same charge date, same interval —
// the backend webhook sync keeps it aligned from then on.
export const createSeasonalSubscription = async (addressId, { externalProductId, externalVariantId, quantity = 1, ...options }) => {
  const session = getRechargeSession();

  const boxSubs = await fetchSubscriptions();
  const boxSub = boxSubs.find(
    (s) => s.address_id === addressId && (s.status || '').toLowerCase() === 'active'
  );
  if (!boxSub || !boxSub.next_charge_scheduled_at) {
    throw new Error('The seasonal bundle requires an active box membership');
  }

  const existingSeasonal = (await fetchSeasonalSubscriptions()).find(
    (s) => s.address_id === addressId && (s.status || '').toLowerCase() === 'active'
  );
  if (existingSeasonal) {
    return existingSeasonal;
  }

  const data = {
    address_id: addressId,
    quantity,
    external_product_id: { ecommerce: String(externalProductId) },
    external_variant_id: { ecommerce: String(externalVariantId) },
    next_charge_scheduled_at: boxSub.next_charge_scheduled_at,
    order_interval_unit: boxSub.order_interval_unit,
    order_interval_frequency: boxSub.order_interval_frequency,
    charge_interval_frequency: boxSub.charge_interval_frequency,
    ...options, // e.g. plan_id when the seasonal product uses a selling plan
  };

  const result = await createSubscriptionSDK(session, data);
  invalidateCache('subscriptions');
  return result;
};

// Retrieve store-wide settings/info using the Recharge SDK `getStore` method
export const getStoreSettings = async () => {
  const session = getRechargeSession();
  return await getStoreSettingsSDK(session);
};

export const cancelSubscription = async (subscriptionId, cancellationReason = '', sendEmail = true) => {
  const session = getRechargeSession();
  const result = await cancelSubscriptionSDK(session, subscriptionId, {
    cancellation_reason: cancellationReason,
    send_email: sendEmail,
  });
  invalidateCache('subscriptions');
  return result;
};

export const updateAddress = async (addressId, updateData) => {
  const session = getRechargeSession();
  const result = await updateAddressSDK(session, addressId, updateData);
  invalidateCache('addresses');
  // Subscriptions embed their shipping address (include.address), so a stale
  // subscriptions cache would keep showing the old address on the account overview.
  invalidateCache('subscriptions');
  return result;
};

export const updateSubscriptionAddress = async (subscriptionId, newAddressId) => {
  const session = getRechargeSession();
  const result = await updateSubscriptionAddressSDK(session, subscriptionId, newAddressId);
  invalidateCache('subscriptions');
  invalidateCache('addresses');
  return result;
};

export const fetchCustomer = async (options = {}) => {
  const customer = await cachedFetch(['customer', options], async () => {
    const session = getRechargeSession();
    try {
      return await readViaProxyFallback(() => getCustomerSDK(session, options));
    } catch (error) {
      console.error('Error fetching customer details:', error);
      throw error;
    }
  }, CUSTOMER_TTL_MS);
  rememberCustomer(customer);
  return customer;
};

// Fetch a specific gift purchase by ID
export const fetchGiftPurchase = async (giftId) => {
  const session = getRechargeSession();
  try {
    return await getGiftPurchase(session, giftId);
  } catch (error) {
    console.error('Error fetching gift purchase:', error);
    throw error;
  }
};

// List all gift purchases for the customer
export const fetchGiftPurchases = async (options = {}) => {
  const session = getRechargeSession();
  try {
    const response = await listGiftPurchases(session, options);
    return response.gift_purchases || [];
  } catch (error) {
    console.error('Error fetching gift purchases:', error);
    throw error;
  }
};

// Create subscription from gift redemption
export const redeemGiftSubscription = async ({ addressId, giftId, productId, variantId, planId, nextChargeDate, quantity = 1, bundleItems = null }) => {
  const session = getRechargeSession();
  
  const data = {
    address_id: addressId,
    gift_id: giftId,
    external_product_id: { ecommerce: productId },
    external_variant_id: { ecommerce: variantId },
    quantity: quantity,
    next_charge_scheduled_at: nextChargeDate,
    charge_interval_frequency: 30,
    order_interval_frequency: 30,
    order_interval_unit: 'day',
  };

  if (planId) {
    data.plan_id = planId;
  }
  
  if (bundleItems && bundleItems.length > 0) {
    data.items = bundleItems;
  }

  console.log('Creating gift subscription with data:', data);

  try {
    const result = await createSubscriptionSDK(session, data);
    invalidateCache('subscriptions');
    return result;
  } catch (error) {
    console.error('Error redeeming gift subscription:', error);
    throw error;
  }
};

/**
 * Fetches portal access with a temporary token for accessing the Recharge hosted portal
 * @param {string} pageDestination - The page to navigate to (e.g., 'payment_methods', 'overview')
 * @returns {Promise<Object>} Portal access object containing temp_token, portal_url, customer_hash, etc.
 */
export const fetchPortalAccess = async (pageDestination = 'payment_methods') => {
  const session = getRechargeSession();

  try {
    const shopUrl = 'glutenbox.myshopify.com'; // Your shop URL
    // Raw fetch (not the SDK), so proxy mode doesn't apply automatically —
    // honor the device flag here and fall back per-call like the SDK paths do.
    const directUrl = `https://api.rechargeapps.com/portal_access?shop_url=${shopUrl}&page_destination=${pageDestination}`;
    const proxiedUrl = `${BASE_API_URL}/api/recharge-proxy/api/portal_access?shop_url=${shopUrl}&page_destination=${pageDestination}`;
    const fetchOptions = {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.apiToken}`,
        'Content-Type': 'application/json',
      },
    };

    let response;
    if (isProxyMode()) {
      response = await fetch(proxiedUrl, fetchOptions);
    } else {
      try {
        response = await fetch(directUrl, fetchOptions);
      } catch (error) {
        if (!isNetworkLevelError(error)) throw error;
        response = await fetch(proxiedUrl, fetchOptions);
      }
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch portal access: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching portal access:', error);
    throw error;
  }
};

/**
 * Initialize Recharge Frictionless Payments (SDK >= 1.80.0).
 * Returns a form controller with mount/submit/unmount used to securely
 * capture card details via Recharge-hosted (Spreedly) fields.
 *
 * Pass an existing Recharge payment method id to edit a card; omit it to
 * create a brand-new payment method (e.g. a backup payment method).
 */
export const initFrictionlessPayment = async (existingPaymentMethodId = undefined) => {
  const session = getRechargeSession();
  return await initFrictionlessPaymentV1(session, existingPaymentMethodId);
};

// Call after a successful frictionless submit so the Account page re-fetches
// the fresh payment method list.
export const invalidatePaymentMethodsCache = () => {
  invalidateCache('paymentMethods');
};

/**
 * After a member saves a new primary card, immediately reprocess the charge
 * that is stuck in Recharge's error (dunning) state. Without this, Recharge
 * waits for its next scheduled retry (up to ~24h) and the portal keeps
 * showing a payment-failure banner even though the card is fixed.
 *
 * Never throws. Resolves to { attempted, success, chargeStatus }.
 */
export const reprocessLatestErrorCharge = async () => {
  try {
    invalidateCache('charges');
    const charges = await fetchCharges();
    const sorted = [...(charges || [])].sort((a, b) =>
      new Date(b.scheduled_at || b.created_at || 0) - new Date(a.scheduled_at || a.created_at || 0)
    );
    // Only retry when the *latest* charge is the errored one — if a newer
    // charge exists, the old error is stale and there is nothing to fix.
    const newest = sorted[0];
    if (!newest || (newest.status || '').toLowerCase() !== 'error') {
      return { attempted: false, success: false, chargeStatus: null };
    }

    let customerId = newest.customer?.id || newest.customer_id;
    if (!customerId) {
      const customer = await fetchCustomer();
      customerId = customer?.id;
    }
    if (!customerId) {
      return { attempted: false, success: false, chargeStatus: null };
    }

    const result = await retryErrorCharge(newest.id, customerId);
    invalidateCache('charges');
    return {
      attempted: Boolean(result?.retried),
      success: result?.charge_status === 'success',
      chargeStatus: result?.charge_status || null,
    };
  } catch (error) {
    console.error('Error reprocessing errored charge after card update:', error);
    invalidateCache('charges');
    return { attempted: true, success: false, chargeStatus: null };
  }
};

/**
 * Customer-level opt-in for Recharge backup payment methods. When enabled,
 * Recharge retries a failed charge against the customer's other stored
 * payment methods. Simply storing a second card is not enough — this flag
 * must be true for fallback to happen.
 */
export const setBackupPaymentMethodsEnabled = async (enabled) => {
  const session = getRechargeSession();
  const result = await updateCustomerSDK(session, { backup_payment_methods_enabled: enabled });
  invalidateCache('customer');
  return result;
};

/**
 * Migrate a subscription from a legacy product to the new unified custom box.
 * This updates the external_product_id and external_variant_id for the subscription.
 * 
 * @param {string} subscriptionId - The subscription ID to migrate
 * @param {string} newProductId - The new product ID (e.g., '7161005736003')
 * @param {string} newVariantId - The new variant ID matching the desired plan/size
 * @returns {Promise<Object>} Updated subscription object
 */
export const migrateSubscriptionToNewProduct = async (subscriptionId, newProductId, newVariantId) => {
  const session = getRechargeSession();
  
  try {
    // First, get the current subscription to find the bundle selection info
    const currentSubResponse = await getSubscription(session, subscriptionId, { include: ['bundle_selections'] });
    const currentSub = currentSubResponse?.subscription || currentSubResponse;
    const purchaseItemId = currentSub?.include?.bundle_selections?.purchase_item_id;
    const bundleSelectionId = currentSub?.include?.bundle_selections?.id;
    
    if (purchaseItemId) {
      // Step 1: Delete the bundle selection to clear all items
      // This forces a complete reset - the subscription will have no bundle selections
      if (bundleSelectionId) {
        try {
          await deleteBundleSelectionSDK(session, bundleSelectionId);
        } catch (e) {
          console.warn('Could not delete bundle selection:', e);
        }
      }
      
      // Step 2: Update the bundle with new product/variant
      // Since we deleted the bundle selection, this creates a fresh bundle
      const result = await updateBundleSDK(session, purchaseItemId, {
        external_product_id: { ecommerce: newProductId },
        external_variant_id: { ecommerce: newVariantId },
      }, { commit: false });
      
      invalidateCache('subscriptions');
      invalidateCache('bundleSelections');
      invalidateBoxPreviews();
      return result;
    } else {
      // Fallback: just update the subscription product/variant
      const updateData = {
        external_product_id: { ecommerce: newProductId },
        external_variant_id: { ecommerce: newVariantId },
      };
      
      const result = await updateSubscriptionSDK(session, subscriptionId, updateData);
      invalidateCache('subscriptions');
      invalidateBoxPreviews();
      return result;
    }
  } catch (error) {
    console.error('Error migrating subscription to new product:', error);
    throw error;
  }
};